const SCORE_TIER_CONFIG = [
    {
        key: 'critical',
        max: 49,
        label: 'Riesgo Crítico',
        color: '#C0392B',
        textColor: '#FFFFFF',
    },
    {
        key: 'high',
        max: 64,
        label: 'Riesgo Alto',
        color: '#E67E22',
        textColor: '#FFFFFF',
    },
    {
        key: 'moderate',
        max: 79,
        label: 'Riesgo Moderado',
        color: '#F9A825',
        textColor: '#333333',
    },
    {
        key: 'low',
        max: 100,
        label: 'Riesgo Bajo',
        color: '#1E8449',
        textColor: '#FFFFFF',
    },
];

// PROFILE_TIER_CONFIG eliminado — getProfileTier ahora delega en SCORE_TIER_CONFIG
// para usar las mismas etiquetas (Riesgo Crítico / Alto / Moderado / Bajo) y umbrales.

export const SELFCARE_RISK_PROFILE_THRESHOLD = 50;
export const SELFCARE_MATRIX_PROTECTIVE_THRESHOLD = 65;
export const SELFCARE_MATRIX_CRITICAL_THRESHOLD = 50;

// mechanismRank: 1 = directo (mecanismo proximal), 2 = indirecto, 3 = distal.
// El peso (weight) de cada dimensión es dinámico: viene del API como weight_percent.
// Desempate de perfiles: score más bajo → mayor weight_percent (API) → menor mechanismRank.
export const SELFCARE_DIMENSION_DEFS = [
    {
        code: 'D1',
        reportOrder: 1,
        radarOrder: 1,
        mechanismRank: 1, // directo: fatiga → error
        reportTitle: 'D1 Bienestar físico y escucha corporal',
        shortTitle: 'Bienestar físico',
        keywords: ['d1', 'bienestar', 'fisico', 'escucha', 'corporal'],
        profileName: 'Fatigado',
        profileText:
            'Este perfil se asocia a señales de cansancio físico, dolor ignorado o sueño insuficiente. Cuando el cuerpo no se recupera, la atención baja, los tiempos de reacción aumentan y la probabilidad de errores por descuido se eleva.',
    },
    {
        code: 'D2',
        reportOrder: 5,
        radarOrder: 2,
        mechanismRank: 1, // directo: impulsividad → acto inseguro
        reportTitle: 'D2 Manejo emocional y autocontrol',
        shortTitle: 'Manejo emocional',
        keywords: ['d2', 'manejo', 'emocional', 'autocontrol'],
        profileName: 'Impulsivo',
        profileText:
            'Este perfil se asocia a operar bajo estados emocionales intensos, como frustración, rabia o estrés. Las decisiones apresuradas bajo presión emocional aumentan la probabilidad de actos inseguros.',
    },
    {
        code: 'D3',
        reportOrder: 3,
        radarOrder: 3,
        mechanismRank: 1, // directo: inatención → error
        reportTitle: 'D3 Conciencia personal y presencia',
        shortTitle: 'Conciencia',
        keywords: ['d3', 'conciencia', 'presencia', 'atencion'],
        profileName: 'Distraído',
        profileText:
            'Este perfil se asocia a trabajar en piloto automático o perder el foco ante distractores. La falta de atención sostenida es uno de los precursores más directos del error humano en tareas repetitivas.',
    },
    {
        code: 'D4',
        reportOrder: 2,
        radarOrder: 4,
        mechanismRank: 2, // indirecto: silencio → riesgo latente
        reportTitle: 'D4 Comunicación y Reporte de Riesgos',
        shortTitle: 'Comunicación',
        keywords: ['d4', 'comunicacion', 'reporte', 'riesgo'],
        profileName: 'Silencioso',
        profileText:
            'Este perfil se asocia a baja comunicación de riesgos. No reportar puede mantener riesgos ocultos y limitar el aprendizaje preventivo del equipo.',
    },
    {
        code: 'D5',
        reportOrder: 4,
        radarOrder: 5,
        mechanismRank: 1, // directo: violación de procedimiento → accidente
        reportTitle: 'D5 Disciplina y hábitos seguros',
        shortTitle: 'Disciplina',
        keywords: ['d5', 'disciplina', 'procedimiento', 'habitos'],
        profileName: 'Indisciplinado',
        profileText:
            'Este perfil se asocia a omisión de pasos del procedimiento, especialmente bajo presión de producción. Los atajos procedimentales son el mecanismo observable más frecuente en las investigaciones de accidentes.',
    },
    {
        code: 'D6',
        reportOrder: 7,
        radarOrder: 6,
        mechanismRank: 2, // indirecto: aislamiento → compañero sin protección
        reportTitle: 'D6 Responsabilidad personal y con el equipo',
        shortTitle: 'Responsabilidad',
        keywords: ['d6', 'responsabilidad', 'equipo', 'grupal'],
        profileName: 'Aislado',
        profileText:
            'Este perfil se asocia a baja responsabilidad grupal: no verificar EPP de compañeros, no pedir ayuda y no transferir conocimiento de seguridad. El aislamiento deja al compañero sin protección.',
    },
    {
        code: 'D7',
        reportOrder: 6,
        radarOrder: 7,
        mechanismRank: 3, // distal: desmotivación → erosión de hábitos
        reportTitle: 'D7 Propósito, motivación y regresar sano a casa',
        shortTitle: 'Propósito',
        keywords: ['d7', 'proposito', 'motivacion', 'casa'],
        profileName: 'Desconectado',
        profileText:
            'Este perfil se asocia a baja motivación intrínseca para cuidarse. Cuando la prioridad es producir y no cuidarse, los hábitos seguros se erosionan gradualmente.',
    },
];

export const SELFCARE_MATRIX_DEFS = [
    {
        id: 'm1_agotamiento_dual',
        number: 1,
        matrixTitle: 'Matriz 1 - Manejo emocional x Bienestar físico',
        xDimension: 'D2',
        yDimension: 'D1',
        xLabel: 'Manejo emocional y autocontrol',
        yLabel: 'Bienestar físico y escucha corporal',
        purpose: 'Agotamiento dual: fatiga y carga emocional simultáneas.',
        criticalInterpretation:
            'La combinación de cansancio y presión emocional reduce recursos mentales y físicos. Es una de las combinaciones más críticas por su asociación a impulsividad y errores múltiples.',
        mixedElevatedInterpretation:
            'Cuando baja el bienestar físico y además hay presión emocional, la capacidad de tomar decisiones seguras se deteriora.',
        whatCanHappen: 'Impulsividad, olvidos, reacción lenta y errores múltiples.',
        criticalMessage: 'Hoy tu cuerpo y mente necesitan pausa.',
        mixedMessage: 'Si estás cansado, revisa dos veces.',
    },
    {
        id: 'm2_riesgo_silencioso',
        number: 2,
        matrixTitle: 'Matriz 2 - Disciplina x Comunicación',
        xDimension: 'D5',
        yDimension: 'D4',
        xLabel: 'Disciplina y hábitos seguros',
        yLabel: 'Comunicación y Reporte de Riesgos',
        purpose: 'Riesgo silencioso: omite procedimientos y no reporta.',
        criticalInterpretation:
            'Omite pasos de procedimiento y no reporta riesgos. Genera y oculta riesgos simultáneamente. Es la combinación más peligrosa para el sistema de seguridad.',
        mixedElevatedInterpretation:
            'Cuando se relaja el cumplimiento y además no se comunican los riesgos, la organización pierde visibilidad de lo que está fallando.',
        whatCanHappen: 'Riesgos ocultos, incidentes repetidos y fallas sistémicas no detectadas.',
        criticalMessage: 'Si ves algo, dilo. Si te saltas un paso, corrígelo ahora.',
        mixedMessage: 'Sigue el paso a paso sin saltos.',
    },
    {
        id: 'm3_rutinizacion',
        number: 3,
        matrixTitle: 'Matriz 3 - Conciencia x Propósito',
        xDimension: 'D3',
        yDimension: 'D7',
        xLabel: 'Conciencia personal y presencia',
        yLabel: 'Propósito, motivación y regresar sano a casa',
        purpose: 'Rutinización: quiere cuidarse pero opera en automático.',
        criticalInterpretation:
            'Baja atención y baja motivación. Operar en automático sin conexión con el autocuidado genera vulnerabilidad sostenida que se acumula con el tiempo.',
        mixedElevatedInterpretation:
            'Cuando la motivación baja, la atención tiende a seguirla. El foco es reconectar el sentido del cuidado con las acciones diarias.',
        whatCanHappen: 'Trabajar en automático sin darse cuenta y omisiones por rutina.',
        criticalMessage: 'Respira y revisa antes de actuar.',
        mixedMessage: 'Tu atención protege tu vida.',
    },
    {
        id: 'm4_fatiga_atajos',
        number: 4,
        matrixTitle: 'Matriz 4 - Bienestar físico x Disciplina',
        xDimension: 'D1',
        yDimension: 'D5',
        xLabel: 'Bienestar físico y escucha corporal',
        yLabel: 'Disciplina y hábitos seguros',
        purpose: 'Fatiga + atajos: cansado y saltando pasos.',
        criticalInterpretation:
            'Fatigado y saltando pasos. El cuerpo cansado busca el camino más corto. Es el predictor directo más fuerte de accidentes por omisión de controles.',
        mixedElevatedInterpretation:
            'Cuando baja el bienestar físico, aumenta la tendencia a buscar atajos. El foco es sostener el estándar incluso con cansancio.',
        whatCanHappen: 'Saltarse pasos, improvisar e incumplir controles o EPP.',
        criticalMessage: 'El cansancio no justifica saltarse reglas.',
        mixedMessage: 'Aunque estés cansado, el procedimiento te protege.',
    },
    {
        id: 'm5_aislamiento_total',
        number: 5,
        matrixTitle: 'Matriz 5 - Comunicación x Responsabilidad',
        xDimension: 'D4',
        yDimension: 'D6',
        xLabel: 'Comunicación y Reporte de Riesgos',
        yLabel: 'Responsabilidad personal y con el equipo',
        purpose: 'Aislamiento total: no reporta y no cuida al compañero.',
        criticalInterpretation:
            'No reporta riesgos ni cuida al compañero. Es una desconexión completa del sistema de seguridad grupal y requiere intervención inmediata.',
        mixedElevatedInterpretation:
            'Cuando baja la comunicación y también la responsabilidad grupal, el equipo pierde su red de protección mutua.',
        whatCanHappen: 'Compañero sin protección, riesgos que nadie reporta y aislamiento operativo.',
        criticalMessage: 'Tu voz protege al equipo.',
        mixedMessage: 'Cuidar al otro es cuidarte a ti.',
    },
];

const MATRIX_ZONE_CONFIG = {
    green: {
        label: 'ZONA PROTECTORA',
        color: '#43A047',
        action: 'Mantener. Estas dos dimensiones están en rango seguro.',
        message: 'Estás en buen camino. Sigue así.',
    },
    yellow: {
        label: 'ZONA MIXTA',
        color: '#FDD835',
        action: 'Reforzar la dimensión que está por debajo de 65. Aplicar pausas y verificación en esa área.',
        message: 'Hay un área que necesita tu atención. Pequeños ajustes hacen la diferencia.',
    },
    orange: {
        label: 'ZONA MIXTA ELEVADA',
        color: '#FB8C00',
        action: 'Ambas dimensiones requieren atención. Priorizar la más baja. Supervisión, checklist y refuerzo de procedimiento.',
        message: 'Hoy necesitas cuidarte más en estas dos áreas. Pide apoyo si lo necesitas.',
    },
    red: {
        label: 'ZONA CRITICA',
        color: '#E53935',
        action: 'Intervención inmediata. No realizar tareas críticas sin acompañamiento. Pausas, rotación y doble verificación obligatoria.',
    },
};

const normalizeText = (value) =>
    String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/[^a-z0-9]/g, '');

const clampScore = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return null;
    return Math.max(0, Math.min(100, numeric));
};

export const roundHalfUp = (value, digits = 1) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return null;
    const factor = 10 ** digits;
    return Math.round((numeric + Number.EPSILON) * factor) / factor;
};

export const roundHalfUpInteger = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return null;
    return Math.round(numeric + Number.EPSILON);
};

const extractDimensionCode = (value) => {
    const match = String(value || '').toUpperCase().match(/D[1-7]/);
    return match ? match[0] : null;
};

const getDimensionDefinitionByCode = (code) => SELFCARE_DIMENSION_DEFS.find((item) => item.code === code) || null;

const inferDimensionDefinition = ({ key, title }) => {
    const code = extractDimensionCode(key) || extractDimensionCode(title);
    if (code) return getDimensionDefinitionByCode(code);

    const normalizedTitle = normalizeText(title);
    return (
        SELFCARE_DIMENSION_DEFS.find((item) => item.keywords.some((keyword) => normalizedTitle.includes(normalizeText(keyword)))) ||
        null
    );
};

const sortDimensions = (items, orderKey) => {
    return [...items].sort((a, b) => {
        const aOrder = a.definition?.[orderKey] ?? 999;
        const bOrder = b.definition?.[orderKey] ?? 999;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return String(a.reportTitle || a.title || '').localeCompare(String(b.reportTitle || b.title || ''));
    });
};

export const getSelfcareScoreTier = (value) => {
    const score = clampScore(value);
    if (score === null) return SCORE_TIER_CONFIG[1];
    return SCORE_TIER_CONFIG.find((item) => score <= item.max) || SCORE_TIER_CONFIG[SCORE_TIER_CONFIG.length - 1];
};

export const getVisibleSelfcareGlobalScore = (individualResult) => {
    const scoreFinal = clampScore(individualResult?.score_final);
    if (scoreFinal !== null) return scoreFinal;

    const ier = clampScore(individualResult?.ier);
    if (ier !== null) return ier;

    return clampScore(individualResult?.average);
};

export const getDimensionsEntries = (individualResult, options = {}) => {
    const { order = 'report' } = options;
    const raw = individualResult?.average_by_dimension;

    let items = [];
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        items = Object.entries(raw)
            .filter(([key]) => !String(key).toLowerCase().includes('general'))
            .map(([key, dim]) => ({ key, ...dim }));
    } else if (Array.isArray(raw)) {
        items = raw
            .map((item, index) => ({
                key: item?.key ?? item?.dimension ?? index,
                ...item,
                title: item?.title ?? item?.dimension_name ?? item?.dimensionTitle ?? item?.name,
                average: item?.average ?? item?.value,
            }))
            .filter((item) => item.title);
    }

    const enriched = items.map((dimension) => {
        const definition = inferDimensionDefinition(dimension);
        const score = clampScore(dimension.average);
        return {
            ...dimension,
            average: score,
            definition,
            code: definition?.code || extractDimensionCode(dimension.key) || extractDimensionCode(dimension.title) || dimension.key,
            reportTitle: definition?.reportTitle || dimension.title,
            shortTitle: definition?.shortTitle || dimension.title,
        };
    });

    return sortDimensions(enriched, order === 'radar' ? 'radarOrder' : 'reportOrder');
};

export const getDimensionByCode = (individualResult, code) => {
    return getDimensionsEntries(individualResult, { order: 'radar' }).find((item) => item.code === code) || null;
};

export const groupDimensionsByRisk = (individualResult) => {
    const dimensions = getDimensionsEntries(individualResult, { order: 'report' });
    const protectors = []; // ≥ 80 — Riesgo Bajo
    const attention = [];  // 65–79 — Riesgo Moderado
    const high = [];       // 50–64 — Riesgo Alto
    const critical = [];   // < 50 — Riesgo Crítico

    dimensions.forEach((dimension) => {
        const score = Number(dimension.average);
        if (!Number.isFinite(score)) return;
        if (score >= 80) protectors.push(dimension);
        else if (score >= 65) attention.push(dimension);
        else if (score >= 50) high.push(dimension);
        else critical.push(dimension);
    });

    return { protectors, attention, high, critical };
};

export const getGlobalNarrative = (value) => {
    const score = Number(value);
    if (!Number.isFinite(score)) return '';
    if (score >= 80) {
        return 'Tu autocuidado es consistente y estable. Tus prácticas físicas, emocionales y de atención se mantienen en un nivel que reduce de forma importante la probabilidad de error. Sigue fortaleciendo estos hábitos.';
    }
    if (score >= 65) {
        return 'Tu autocuidado es adecuado en general, pero hay dimensiones específicas que presentan brechas. Identificarlas y trabajarlas te permitirá fortalecer tu seguridad y la de tu equipo.';
    }
    if (score >= 50) {
        return 'Tu autocuidado muestra variabilidad. Algunas prácticas son sólidas, pero otras representan vulnerabilidades. Esto incrementa la probabilidad de errores en determinadas condiciones operativas. Requieres acompañamiento focalizado para fortalecer hábitos clave.';
    }
    return 'Tu autocuidado presenta brechas importantes en varias dimensiones. Esto eleva significativamente la probabilidad de error humano. Es prioritario que recibas acompañamiento inmediato y que se revisen las condiciones de trabajo que pueden estar contribuyendo a esta situación.';
};

export const getProfileTier = (level) => {
    // level = 100 - score_dim; convertimos de vuelta a score_dim para usar
    // los mismos umbrales y etiquetas que SCORE_TIER_CONFIG (spec IASH V5.2).
    const safeLevel = clampScore(level);
    if (safeLevel === null) return SCORE_TIER_CONFIG[SCORE_TIER_CONFIG.length - 1];
    const scoreDim = 100 - safeLevel;
    return getSelfcareScoreTier(scoreDim);
};

export const getResolvedSelfcareRiskProfiles = (individualResult) => {
    // Orden de perfiles: score más bajo primero.
    // Desempate 1: mayor weight (poder predictivo de accidentalidad, Ficha Técnica V5.2).
    // Desempate 2: menor mechanismRank (1=directo > 2=indirecto > 3=distal).
    return getDimensionsEntries(individualResult, { order: 'radar' })
        .map((dimension) => {
            const score = clampScore(dimension.average) ?? 0;
            const level = 100 - score;
            const tier = getProfileTier(level);
            return {
                key: dimension.code,
                title: dimension.definition?.profileName || dimension.shortTitle,
                shortTitle: dimension.definition?.profileName || dimension.shortTitle,
                factor: dimension.reportTitle,
                score,
                level,
                weight: dimension.weight_percent ?? 0,   // dinámico: viene del API
                mechanismRank: dimension.definition?.mechanismRank ?? 99, // estático: propiedad del instrumento
                tierKey: tier.key,
                tierLabel: tier.label,
                color: tier.color,
                description: dimension.definition?.profileText || '',
            };
        })
        .sort((a, b) => {
            // 1. Score más bajo (level más alto)
            if (b.level !== a.level) return b.level - a.level;
            // 2. Mayor peso
            if (b.weight !== a.weight) return b.weight - a.weight;
            // 3. Mecanismo más directo
            return a.mechanismRank - b.mechanismRank;
        });
};

export const getPrimarySelfcareProfiles = (profiles) => {
    if (!profiles || profiles.length === 0) return [];
    const topLevel = profiles[0].level;
    return profiles.filter((profile) => profile.level === topLevel);
};

export const getSecondarySelfcareProfiles = (profiles) => {
    if (!profiles || profiles.length < 2) return [];
    const primaryProfiles = getPrimarySelfcareProfiles(profiles);
    // If there are multiple primaries tied at the same level, the secondary is the second one
    if (primaryProfiles.length > 1) {
        return [primaryProfiles[1]];
    }
    // Otherwise, pick the next level down
    const primaryLevel = profiles[0].level;
    const next = profiles.find((profile) => profile.level < primaryLevel);
    if (!next) return [];
    return profiles.filter((profile) => profile.level === next.level);
};

export const getMatrixZone = (xScore, yScore) => {
    const x = clampScore(xScore);
    const y = clampScore(yScore);
    if (x === null || y === null) {
        return {
            key: 'orange',
            label: 'ZONA MIXTA ELEVADA',
            color: MATRIX_ZONE_CONFIG.orange.color,
        };
    }
    if (x >= SELFCARE_MATRIX_PROTECTIVE_THRESHOLD && y >= SELFCARE_MATRIX_PROTECTIVE_THRESHOLD) {
        return { key: 'green', label: MATRIX_ZONE_CONFIG.green.label, color: MATRIX_ZONE_CONFIG.green.color };
    }
    if (x < SELFCARE_MATRIX_CRITICAL_THRESHOLD && y < SELFCARE_MATRIX_CRITICAL_THRESHOLD) {
        return { key: 'red', label: MATRIX_ZONE_CONFIG.red.label, color: MATRIX_ZONE_CONFIG.red.color };
    }
    if (x < SELFCARE_MATRIX_PROTECTIVE_THRESHOLD && y < SELFCARE_MATRIX_PROTECTIVE_THRESHOLD) {
        return { key: 'orange', label: MATRIX_ZONE_CONFIG.orange.label, color: MATRIX_ZONE_CONFIG.orange.color };
    }
    return { key: 'yellow', label: MATRIX_ZONE_CONFIG.yellow.label, color: MATRIX_ZONE_CONFIG.yellow.color };
};

export const getMatrixInterpretation = (matrixDef, zoneKey) => {
    if (zoneKey === 'red') return matrixDef.criticalInterpretation;
    if (zoneKey === 'orange') return matrixDef.mixedElevatedInterpretation;
    if (zoneKey === 'yellow') return 'Existe vulnerabilidad parcial en una de las dos dimensiones del cruce. El foco es reforzar la dimensión que hoy está por debajo del umbral seguro.';
    return 'Ambas dimensiones se encuentran en rango seguro. La prioridad es sostener estas practicas protectoras y mantener seguimiento preventivo.';
};

export const getMatrixAction = (zoneKey) => MATRIX_ZONE_CONFIG[zoneKey]?.action || MATRIX_ZONE_CONFIG.yellow.action;

export const getMatrixMessage = (matrixDef, zoneKey) => {
    if (zoneKey === 'red') {
        return `${matrixDef.criticalMessage} Habla con tu supervisor o con el equipo de seguridad.`;
    }
    if (zoneKey === 'orange') return matrixDef.mixedMessage;
    return MATRIX_ZONE_CONFIG[zoneKey]?.message || MATRIX_ZONE_CONFIG.yellow.message;
};

export const getResolvedSelfcareMatrices = (individualResult) => {
    return SELFCARE_MATRIX_DEFS.map((definition) => {
        const xDimension = getDimensionByCode(individualResult, definition.xDimension);
        const yDimension = getDimensionByCode(individualResult, definition.yDimension);
        const x = xDimension?.average ?? null;
        const y = yDimension?.average ?? null;
        const zone = getMatrixZone(x, y);
        return {
            def: definition,
            x,
            y,
            zone,
            interpretation: getMatrixInterpretation(definition, zone.key),
            action: getMatrixAction(zone.key),
            message: getMatrixMessage(definition, zone.key),
        };
    });
};

export const getVisibleSelfcareMatrices = (individualResult) => getResolvedSelfcareMatrices(individualResult);

export const getRecommendationMatrices = (individualResult) => {
    const zonePriority = { red: 3, orange: 2, yellow: 1, green: 0 };
    return getResolvedSelfcareMatrices(individualResult)
        .filter((item) => item.zone.key !== 'green')
        .sort((a, b) => {
            const priorityDiff = zonePriority[b.zone.key] - zonePriority[a.zone.key];
            if (priorityDiff !== 0) return priorityDiff;
            const sumA = (a.x || 0) + (a.y || 0);
            const sumB = (b.x || 0) + (b.y || 0);
            return sumA - sumB;
        });
};

export const getPredictiveMatricesSelfcarePageCount = () => SELFCARE_MATRIX_DEFS.length;

export const getRecommendationsSelfcarePageCount = (individualResult) => getRecommendationMatrices(individualResult).length;
